import { useRef } from "react";

import { Icon } from "@/components/common/Icon";
import { Checkbox, Field } from "@/components/onboarding/OnbFields";
import { Button } from "@/components/common";

import type { OrgForm, OrgSetField } from "./types";

interface Props {
  form: OrgForm;
  setField: OrgSetField;
}

export function OrgStepCertificado({ form, setField }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onPick = (file: File | null) => {
    setField("certFile", file);
    setField("certNome", file ? file.name : "");
  };

  return (
    <>
      <div className="onb-section-head">
        <div className="onb-step-eyebrow">
          ETAPA 2 · OPCIONAL · MAS NECESSÁRIA PARA OPERAR
        </div>
        <h1 className="onb-step-title">Certificado digital e procurações</h1>
        <p className="onb-step-sub">
          Anexe seu certificado A1 do CNPJ pra que o TDAX consiga acessar o
          e-CAC em nome da organização e{" "}
          <strong>criar todas as empresas do grupo automaticamente</strong> —
          sem precisar cadastrar uma a uma.
        </p>
      </div>

      <div className="onb-callout">
        <div className="onb-callout-icon is-warning">
          <Icon name="alert-triangle" size={16} />
        </div>
        <div>
          <div className="onb-callout-title">
            Sem certificado, a organização fica inerte
          </div>
          <div className="onb-callout-sub">
            Procurações eletrônicas, importação automática de CNPJs, transmissão
            de arquivos, consulta de PER/DCOMP e diagnósticos dependem desse
            acesso. Você pode anexar agora ou voltar depois nas configurações da
            org.
          </div>
        </div>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Certificado digital (A1)</div>
        <div className="onb-file-drop">
          <Icon name="file-text" size={22} />
          <div className="onb-file-drop-text">
            <div className="onb-file-drop-title">
              {form.certNome ||
                "Arraste o arquivo .pfx ou clique para selecionar"}
            </div>
            <div className="onb-file-drop-sub">
              {form.certNome
                ? "pronto · trocar arquivo"
                : "aceitamos .pfx ou .p12 · até 4 MB"}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pfx,.p12"
            style={{ display: "none" }}
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
          <Button variant="ghost" onClick={() => fileRef.current?.click()}>
            {form.certNome ? "Trocar" : "Selecionar arquivo"}
          </Button>
        </div>

        {form.certNome && (
          <div className="onb-field-grid onb-grid-2 onb-grid-mt-md">
            <Field label="Senha do certificado" required>
              <input
                className="onb-input"
                type="password"
                maxLength={120}
                value={form.certSenha}
                onChange={(e) => setField("certSenha", e.target.value)}
                placeholder="••••••••"
              />
            </Field>
            <Field label="Validade" hint="detectada do certificado após o upload">
              <input className="onb-input" value={form.certValidade || "—"} disabled />
            </Field>
          </div>
        )}
      </div>

      <div className="onb-section onb-section-terms">
        <Checkbox
          checked={form.certAceiteLgpd}
          onChange={(v) => setField("certAceiteLgpd", v)}
        >
          Autorizo o Tributo Devido a armazenar o certificado e senha
          criptografados em ambiente seguro e usá-los{" "}
          <strong>exclusivamente</strong> para acessar o e-CAC em nome do CNPJ
          informado. Posso revogar a qualquer momento.
        </Checkbox>
      </div>

      <div className="onb-info-grid">
        <div className="onb-info-card">
          <span className="onb-info-icon">
            <Icon name="shield-check" size={14} />
          </span>
          <div className="onb-info-title">Criptografia em repouso</div>
          <div className="onb-info-sub">
            Senha e arquivo guardados com KMS dedicado. Apenas seu workspace
            acessa.
          </div>
        </div>
        <div className="onb-info-card">
          <span className="onb-info-icon">
            <Icon name="check" size={14} />
          </span>
          <div className="onb-info-title">Importação automática</div>
          <div className="onb-info-sub">
            Após anexar, suas empresas são criadas a partir do que o e-CAC
            retornar.
          </div>
        </div>
        <div className="onb-info-card">
          <span className="onb-info-icon">
            <Icon name="log-out" size={14} />
          </span>
          <div className="onb-info-title">Revogação em 1 clique</div>
          <div className="onb-info-sub">
            Remove o certificado e suspende as automações imediatamente.
          </div>
        </div>
      </div>
    </>
  );
}
